// v2.19.34 — Section "Mes Maisons Nommées" pour l'onglet Adresses de /mon-compte.
// Liste les claims de l'user via GET /api/v3/addresses/my-claims + bouton
// "+ Revendiquer mon adresse" qui ouvre le wizard ClaimAddressWizard.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import ClaimAddressWizard from "./ClaimAddressWizard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const HOST = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

function _authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function _shareUrl(c) {
  // /a/<slug>-<5 derniers chars du address_civ>
  const suffix = c.address_civ.split("-").pop();
  return `${HOST}/a/${c.friendly_name_slug}-${suffix.toLowerCase()}`;
}

export default function MyNamedAddressesSection({ isAuthenticated }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v3/addresses/my-claims`, { headers: _authHeaders() });
      const d = await r.json();
      if (r.ok && d.ok) setClaims(d.claims || []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(_shareUrl(c));
      toast.success("📋 Lien copié");
    } catch (_) { toast.error("Impossible de copier"); }
  };

  const toggleVisibility = async (c) => {
    try {
      const r = await fetch(`${API}/v3/addresses/visibility`, {
        method: "PATCH", headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({ address_civ: c.address_civ, is_public: !c.is_public }),
      });
      const d = await r.json();
      if (r.ok && d.ok) { toast.success(`Visibilité : ${d.is_public ? "publique" : "privée"}`); load(); }
      else toast.error(d.error || "Erreur");
    } catch (_) { toast.error("Erreur réseau"); }
  };

  const toggleSharePhoto = async (c) => {
    try {
      const r = await fetch(`${API}/v3/addresses/share-photo`, {
        method: "PATCH", headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({ address_civ: c.address_civ, share_photo: !c.share_photo }),
      });
      const d = await r.json();
      if (r.ok && d.ok) { toast.success(`Photo dans partages : ${d.share_photo ? "OUI" : "NON"}`); load(); }
      else toast.error(d.error || "Erreur");
    } catch (_) { toast.error("Erreur réseau"); }
  };

  const rename = async (c) => {
    const nv = window.prompt(`Renommer "${c.friendly_name}" en :`, c.friendly_name);
    if (!nv || nv.trim() === c.friendly_name) return;
    try {
      const r = await fetch(`${API}/v3/addresses/rename`, {
        method: "POST", headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({ address_civ: c.address_civ, friendly_name: nv.trim() }),
      });
      const d = await r.json();
      if (r.ok && d.ok) { toast.success(`Renommée en ${d.friendly_name}`); load(); }
      else if (d.error === "rename_cooldown") toast.error(`Tu peux renommer dans ${d.remaining_days}j`);
      else toast.error(d.error || "Erreur");
    } catch (_) { toast.error("Erreur réseau"); }
  };

  if (!isAuthenticated) return null;

  return (
    <section data-testid="moncompte-named-section" style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 8, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>🏠 Mes Maisons Nommées</h2>
        <button onClick={() => setWizardOpen(true)} data-testid="claim-open-wizard" style={addBtnStyle}>
          ➕ Revendiquer mon adresse
        </button>
      </div>

      <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 10 }}>
        Donne un nom mémorable à ton adresse ("Maison Koffi", "Boutique Aya"). Partage-la sur WhatsApp pour que tes proches et les livreurs te retrouvent facilement.
      </div>

      {loading && <div style={{ color: "#94A3B8", fontSize: 13 }}>Chargement…</div>}
      {!loading && claims.length === 0 && (
        <div data-testid="moncompte-named-empty" style={emptyStyle}>
          Aucune maison nommée. Clique sur "Revendiquer mon adresse" pour démarrer.
        </div>
      )}
      {!loading && claims.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {claims.map((c) => (
            <div key={c.address_civ} data-testid={`claim-card-${c.address_civ}`} style={cardStyle}>
              {/* v3.0.0-rc.8g — Modèle PADA (Plan d'Adresse Digitale d'Abidjan)
                  en tête, code AYA CIV en dessous comme référence secondaire. */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#FBBF24" }}>{c.friendly_name}</div>
                  <div
                    data-testid={`claim-pada-${c.address_civ}`}
                    style={{
                      marginTop: 10,
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(99,102,241,0.06))",
                      border: "1px solid rgba(147,197,253,0.35)",
                    }}
                  >
                    <div style={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8,
                      textTransform: "uppercase", color: "#93C5FD", marginBottom: 6,
                      display: "flex", alignItems: "center", gap: 6,
                    }}>
                      <span aria-hidden>🇨🇮</span>
                      <span>Adresse PADA · République de Côte d&apos;Ivoire</span>
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.55, color: "#E2E8F0" }}>
                      <div><strong style={{ color: "#FBBF24" }}>{c.friendly_name}</strong></div>
                      <div>Commune de <strong>{c.commune_name || "—"}</strong>{c.quartier ? `, ${c.quartier}` : ""}</div>
                      {c.street && <div>{c.street}{c.house_number ? `, n° ${c.house_number}` : ""}</div>}
                      {c.ilot && <div>Îlot {c.ilot}{c.lot ? `, Lot ${c.lot}` : ""}</div>}
                      {(c.lat && c.lng) && (
                        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
                          GPS : {Number(c.lat).toFixed(5)}, {Number(c.lng).toFixed(5)}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Code AYA — référence secondaire sous le PADA */}
                  <div style={{
                    marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                  }}>
                    <span style={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6,
                      textTransform: "uppercase", color: "#94A3B8",
                    }}>Code AYA</span>
                    <code
                      data-testid={`claim-civ-${c.address_civ}`}
                      style={{
                        fontFamily: "ui-monospace, Menlo, monospace",
                        fontSize: 12, fontWeight: 700,
                        padding: "3px 8px",
                        background: "rgba(251,191,36,0.14)",
                        border: "1px solid rgba(251,191,36,0.35)",
                        borderRadius: 6, color: "#FBBF24",
                      }}
                    >{c.address_civ}</code>
                  </div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>
                    {c.claim_type === "proprietaire" ? "🏠 Propriétaire" : c.claim_type === "locataire" ? "🔑 Locataire" : "👤 Occupant"}
                    {c.popularity_score > 0 && <> · 🔥 {c.popularity_score}</>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span data-testid={`claim-public-badge-${c.address_civ}`} style={c.is_public ? badgePubStyle : badgePrivStyle}>
                    {c.is_public ? "🌍 Publique" : "🔒 Privée"}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                <button onClick={() => copyLink(c)} data-testid={`claim-copy-${c.address_civ}`} style={miniBtnStyle}>📋 Copier le lien</button>
                <button onClick={() => rename(c)} data-testid={`claim-rename-${c.address_civ}`} style={miniBtnStyle}>✏️ Renommer</button>
                <button onClick={() => toggleVisibility(c)} data-testid={`claim-toggle-public-${c.address_civ}`} style={miniBtnStyle}>{c.is_public ? "🔒 Rendre privée" : "🌍 Rendre publique"}</button>
                <button onClick={() => toggleSharePhoto(c)} disabled={!c.is_public} data-testid={`claim-toggle-share-photo-${c.address_civ}`} style={{ ...miniBtnStyle, opacity: c.is_public ? 1 : 0.4 }}>
                  {c.share_photo ? "📸 Photo dans partage : ON" : "📸 Photo dans partage : OFF"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ClaimAddressWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onSuccess={() => load()} />
    </section>
  );
}

const addBtnStyle = { background: "#FBBF24", color: "#0F172A", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, boxShadow: "0 3px 10px rgba(251,191,36,0.3)" };
const emptyStyle = { background: "rgba(251,191,36,0.06)", border: "1px dashed rgba(251,191,36,0.3)", borderRadius: 12, padding: 18, textAlign: "center", color: "#FBBF24", fontSize: 13 };
const cardStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: 14 };
const badgePubStyle = { fontSize: 11, padding: "3px 10px", borderRadius: 999, background: "rgba(34,197,94,0.18)", color: "#86EFAC", fontWeight: 700, border: "1px solid rgba(34,197,94,0.4)" };
const badgePrivStyle = { fontSize: 11, padding: "3px 10px", borderRadius: 999, background: "rgba(148,163,184,0.18)", color: "#94A3B8", fontWeight: 700, border: "1px solid rgba(148,163,184,0.4)" };
const miniBtnStyle = { background: "rgba(255,255,255,0.06)", color: "#E8ECF7", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer" };
